import { Phone, Mail, MapPin, Clock, Facebook } from "lucide-react";
import { openCookieSettings } from "@/components/CookieConsent";
import { useLanguage } from "@/i18n/LanguageContext";
import { routes } from "@/i18n/routes";
import { usePageContext } from "vike-react/usePageContext";
import { navigate } from "vike/client/router";

const Footer = () => {
  const pageContext = usePageContext();
  const { t, language } = useLanguage();

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    e.preventDefault();
    const homePath = `/${language}`;
    if (pageContext.urlPathname !== homePath && pageContext.urlPathname !== homePath + "/") {
      navigate(homePath);
      setTimeout(() => { document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }); }, 100);
    } else {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <div className="mb-6">
              <span className="text-2xl font-bold">GENDERA</span>
              <span className="text-2xl font-light ml-1 text-primary-foreground/70">SERVICE</span>
            </div>
            <p className="text-primary-foreground/80 leading-relaxed">{t("footer.description")}</p>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-6">{t("footer.quickLinks")}</h3>
            <ul className="space-y-3">
              <li><a href={`/${language}#home`} onClick={(e) => handleNavClick(e, "home")} className="text-primary-foreground/80 hover:text-accent transition-colors cursor-pointer">{t("nav.home")}</a></li>
              <li><a href={`/${language}#about`} onClick={(e) => handleNavClick(e, "about")} className="text-primary-foreground/80 hover:text-accent transition-colors cursor-pointer">{t("nav.about")}</a></li>
              <li><a href={`/${language}#services`} onClick={(e) => handleNavClick(e, "services")} className="text-primary-foreground/80 hover:text-accent transition-colors cursor-pointer">{t("nav.services")}</a></li>
              <li><a href={`/${language}#contact`} onClick={(e) => handleNavClick(e, "contact")} className="text-primary-foreground/80 hover:text-accent transition-colors cursor-pointer">{t("nav.contact")}</a></li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-6">{t("footer.services")}</h3>
            <ul className="space-y-3">
              <li className="text-primary-foreground/80">{t("footer.computerDiagnostics")}</li>
              <li className="text-primary-foreground/80">{t("footer.ecuRepair")}</li>
              <li className="text-primary-foreground/80">{t("footer.truckService")}</li>
              <li className="text-primary-foreground/80">{t("footer.partsElectronic")}</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-6">{t("footer.contact")}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3"><MapPin className="w-5 h-5 mt-1 text-accent" /><span className="text-primary-foreground/80">ul. Tysiąclecia 38<br />62-002 Golęczewo, {t("contact.country")}</span></li>
              <li className="flex items-center gap-3"><Phone className="w-5 h-5 text-accent" /><a href="tel:+48502625857" className="text-primary-foreground/80 hover:text-accent transition-colors">+48 502 625 857</a></li>
              <li className="flex items-center gap-3"><Mail className="w-5 h-5 text-accent" /><a href="mailto:biuro@gendera-service.pl" className="text-primary-foreground/80 hover:text-accent transition-colors">biuro@gendera-service.pl</a></li>
              <li className="flex items-center gap-3"><Clock className="w-5 h-5 text-accent" /><span className="text-primary-foreground/80">{t("footer.workingHours")}</span></li>
              <li className="flex items-center gap-3"><Facebook className="w-5 h-5 text-accent" /><a href="https://www.facebook.com/people/Gendera-Service/100087244996134/" target="_blank" rel="noopener noreferrer" className="text-primary-foreground/80 hover:text-accent transition-colors">Facebook</a></li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-primary-foreground/20">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-primary-foreground/60 text-sm">{t("footer.copyright")}</p>
            <div className="flex gap-6 text-sm">
              <a href={`/${language}/${routes.privacyPolicy[language]}`} className="text-primary-foreground/60 hover:text-accent transition-colors">{t("footer.privacy")}</a>
              <button onClick={openCookieSettings} className="text-primary-foreground/60 hover:text-accent transition-colors">{t("footer.cookies")}</button>
              <a href={`/${language}/${routes.terms[language]}`} className="text-primary-foreground/60 hover:text-accent transition-colors">{t("footer.terms")}</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
